import { Category } from '../types/Category';
import { Content } from '../types/Content';
import { contentSearchablePlainText } from './contentRichPreviewHtml';
import {
  branchChildrenAreLoaded,
  categoryContentsAreLoaded,
  categoryShowsBranchExpandable,
} from './categoryLoadState';

/**
 * NFKC, trim, strip zero‑width / NBSP, collapse whitespace — preserves casing.
 * Use for “no visible change?” when saving renames (case-only edits must still persist).
 */
export const normalizePresentation = (s: string): string =>
  (s || '')
    .normalize('NFKC')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** All branch category nodes nested under `root` (not including `root`). Walks `children` only. */
export function collectDescendantCategories(root: Category): Category[] {
  const out: Category[] = [];
  const walk = (n: Category) => {
    for (const ch of n.children ?? []) {
      out.push(ch);
      walk(ch);
    }
  };
  walk(root);
  return out;
}

/** Sibling/parent duplicate checks — same presentation rules as {@link normalizePresentation}, then lowercase. */
export const normalizeCategoryName = (name: string): string =>
  normalizePresentation(name).toLowerCase();

/** True if `name` is non-empty after normalize and equals one of sibling names under the parent. */
export const isDuplicateName = (name: string, siblings: Category[]): boolean => {
  const nn = normalizeCategoryName(name);
  if (!nn) return false;
  return siblings.some((cat) => normalizeCategoryName(cat.name) === nn);
};

/**
 * True when `name` cannot be placed under `parent`: matches parent name or an existing sibling
 * (optional sibling id excluded — e.g. the node being moved).
 */
export const categoryNameConflictsUnderParent = (
  childName: string,
  parent: Category,
  excludeSiblingId?: string,
): boolean => {
  const normalizedChild = normalizeCategoryName(childName);
  if (!normalizedChild) return false;
  if (normalizeCategoryName(parent.name) === normalizedChild) return true;
  const list = excludeSiblingId
    ? (parent.children ?? []).filter((c) => c.id !== excludeSiblingId)
    : parent.children ?? [];
  return isDuplicateName(childName, list);
};

/**
 * Validates names when pasting/dropping multiple categories into a branch category.
 * Each name must be unique in the batch, must not conflict under the target branch,
 * and must not match a sibling of the target branch (other children of its parent).
 * Source categories are excluded from both checks (they are moving/copying, not fixed siblings).
 */
export const validateMultiCategoryPasteIntoBranchNames = (
  sourceCategories: Category[],
  targetBranch: Category,
  parentOfTarget: Category | null,
): string | null => {
  const sourceIds = new Set(sourceCategories.map((s) => s.id));
  const seen = new Set<string>();
  for (const source of sourceCategories) {
    const normalized = normalizeCategoryName(source.name);
    if (!normalized) {
      return 'Cannot paste: a selected category has an empty name.';
    }
    if (seen.has(normalized)) {
      return 'Cannot paste: selected categories include duplicate names.';
    }
    seen.add(normalized);
    if (categoryNameConflictsUnderParent(source.name, targetBranch, source.id)) {
      return 'Cannot paste category here: name must not match categories already under the target.';
    }
  }
  if (parentOfTarget?.children) {
    const siblingsOfTarget = parentOfTarget.children.filter(
      (c) => c.id !== targetBranch.id && !sourceIds.has(c.id),
    );
    for (const source of sourceCategories) {
      if (isDuplicateName(source.name, siblingsOfTarget)) {
        return 'Cannot paste here: a category name matches a sibling of the target parent.';
      }
    }
  }
  return null;
};

/** True if `descendant` is nested under `ancestor` in the category tree. */
export function isDescendantCategory(ancestor: Category, descendant: Category): boolean {
  if (!ancestor.children?.length) {
    return false;
  }
  return ancestor.children.some(
    (c) => c.id === descendant.id || isDescendantCategory(c, descendant),
  );
}

export function updateCategoryStructure(
  categories: Category[],
  draggedCategory: Category,
  targetCategory: Category,
  position: 'before' | 'after' | 'inside'
): Category[] {
  const removeCategoryId = (ids: string[], idToRemove: string): string[] => {
    return ids.filter(id => id !== idToRemove);
  };

  const removeCategory = (categories: Category[], categoryToRemove: Category): Category[] => {
    return categories.filter(cat => {
      if (cat.id === categoryToRemove.id) {
        return false;
      }
      if (cat.children) {
        cat.children = removeCategory(cat.children, categoryToRemove);
      }
      return true;
    });
  };

  const insertCategory = (cats: Category[], categoryToInsert: Category, target: Category, pos: 'before' | 'after' | 'inside'): Category[] => {
    return cats.map(cat => {
      if (cat.id === target.id) {
        if (pos === 'inside') {
          return {
            ...cat,
            children: [...(cat.children || []), categoryToInsert]
          };
        }
      }
      if (cat.children) {
        cat.children = insertCategory(cat.children, categoryToInsert, target, pos);
      }
      return cat;
    });
  };

  // First remove the dragged category from its current position
  const categoriesWithoutDragged = removeCategory([...categories], draggedCategory);

  // Then insert it at the new position
  return insertCategory(categoriesWithoutDragged, draggedCategory, targetCategory, position);
}

/**
 * Checks if content with the same subcase and text already exists in a category
 * @param category The category to check
 * @param subcase The subcase to check
 * @param text The text content to check
 * @param excludeContentId Optional content ID to exclude from the check (for editing existing content)
 * @returns true if duplicate exists, false otherwise
 */
type ContentMediaFields = Pick<Content, 'attachmentsJson' | 'richTextHtml' | 'richTextDelta'>;

/** True when the row has uploaded attachments and/or an inline image in rich text. */
export function contentHasMedia(content: ContentMediaFields): boolean {
  if (content.attachmentsJson?.trim()) {
    try {
      const arr = JSON.parse(content.attachmentsJson) as unknown;
      if (Array.isArray(arr) && arr.length > 0) {
        return true;
      }
    } catch {
      /* ignore malformed JSON */
    }
  }
  const html = content.richTextHtml ?? '';
  if (/<img\b/i.test(html)) {
    return true;
  }
  const delta = content.richTextDelta ?? '';
  if (/"image"\s*:/.test(delta)) {
    return true;
  }
  return false;
}

/** Short tooltip for the content-pane media indicator. */
export function getContentMediaIndicatorTooltip(content: ContentMediaFields): string {
  if (!contentHasMedia(content)) {
    return '';
  }
  const kinds = new Set<string>();
  if (/<img\b/i.test(content.richTextHtml ?? '') || /"image"\s*:/.test(content.richTextDelta ?? '')) {
    kinds.add('embedded image');
  }
  try {
    const arr = JSON.parse(content.attachmentsJson || '[]') as { mimeType?: string }[];
    if (Array.isArray(arr)) {
      for (const a of arr) {
        const mime = (a.mimeType || '').toLowerCase();
        if (mime.startsWith('image/')) {
          kinds.add('attached image');
        } else if (mime.startsWith('video/')) {
          kinds.add('video');
        } else if (mime.startsWith('audio/')) {
          kinds.add('audio');
        } else if (a.mimeType) {
          kinds.add('media file');
        }
      }
    }
  } catch {
    /* ignore */
  }
  if (kinds.size === 0) {
    return 'This row has media (image, video, or audio)';
  }
  return `Media: ${Array.from(kinds).join(', ')}`;
}

/**
 * Normalizes stored content text the same way as the server ({@link bulk create} / duplicate prevention).
 */
export function contentDedupeKey(subcase: string, text: string): string {
  const sc = (subcase ?? '').trim();
  let t = (text ?? '').trim();
  if (!t) {
    t = ' ';
  }
  return `${sc}\u0000${t}`;
}

export function contentDedupeKeyForContent(
  content: Pick<Content, 'subcase' | 'text'> & { richTextHtml?: string | null },
): string {
  return contentDedupeKey(
    content.subcase ?? '',
    contentSearchablePlainText(content.text ?? '', content.richTextHtml),
  );
}

export const hasDuplicateContent = (
  category: Category,
  subcase: string,
  text: string,
  excludeContentId?: string,
  richTextHtml?: string | null,
): boolean => {
  if (!category.contents) return false;
  const key = contentDedupeKeyForContent({ subcase, text, richTextHtml });

  return category.contents.some(content => {
    if (excludeContentId && content.id === excludeContentId) {
      return false;
    }
    return contentDedupeKeyForContent(content) === key;
  });
};

/** User-visible label for a duplicate content row (subcase + searchable text snippet). */
export function formatDuplicateContentLabel(
  content: Pick<Content, 'subcase' | 'text'> & { richTextHtml?: string | null },
): string {
  const preview = contentSearchablePlainText(content.text ?? '', content.richTextHtml);
  const display =
    preview === ' '
      ? '(empty text)'
      : preview.length > 50
        ? `${preview.substring(0, 50)}…`
        : preview;
  return `"${content.subcase}" — ${display}`;
}

/**
 * Gets a user-friendly message for duplicate content
 * @param subcase The subcase that has a duplicate
 * @returns A formatted warning message
 */
export const getDuplicateContentMessage = (subcase: string): string => {
  return `Content with subcase "${subcase}" and the same text already exists in this category. Please modify the content or choose a different subcase.`;
};

export const TEMP_CONTENT_CATEGORY_PREFIX = 'Temp - ';

/** Stash for file rows when creating a document tree without content (suggest tree from files). */
export const ORIGINAL_FILES_CONTENT_CATEGORY_NAME = 'Original Files Content';

export function findCategoryInTree(categories: Category[], targetId: string): Category | null {
  for (const cat of categories) {
    if (cat.id === targetId) return cat;
    if (cat.children?.length) {
      const found = findCategoryInTree(cat.children, targetId);
      if (found) return found;
    }
  }
  return null;
}

/** True when removing `idsToRemove` from `parent` leaves at least two children, or removes every child. */
export function wouldLeaveAtLeastTwoChildrenAfterRemoving(
  parent: Category,
  idsToRemove: Set<string>,
): boolean {
  const children = parent.children ?? [];
  const remaining = children.filter((c) => !idsToRemove.has(c.id)).length;
  if (remaining >= 2) return true;
  if (remaining === 0 && children.length > 0 && children.every((c) => idsToRemove.has(c.id))) {
    return true;
  }
  return false;
}

/** Branch categories require at least two siblings; cannot delete if one would remain. */
export function canDeleteCategoryUnderParent(
  parent: Category | null | undefined,
  categoryId: string,
): boolean {
  if (!parent?.children) return false;
  return wouldLeaveAtLeastTwoChildrenAfterRemoving(parent, new Set([categoryId]));
}

/** Content sources grouped by ancestor folder on the path from target up to the document root. */
export type AddMatchingContentSourceGroup = {
  ancestorId: string;
  ancestorName: string;
  /** 0 = immediate parent of target, then outward toward root. */
  depth: number;
  sources: Category[];
};

export function collectAddMatchingContentSourcesAlongAncestorPath(
  categoriesTree: Category[],
  target: Category,
): AddMatchingContentSourceGroup[] {
  if (!target.contentCategory) {
    return [];
  }
  const groups: AddMatchingContentSourceGroup[] = [];
  const seenSourceIds = new Set<string>();
  let depth = 0;
  let parentId = target.parentId;
  while (parentId) {
    const parent = findCategoryInTree(categoriesTree, parentId);
    if (!parent) {
      break;
    }
    const sources = (parent.children ?? []).filter(
      (c) =>
        c.contentCategory &&
        c.id !== target.id &&
        contentCategoryMayHaveRows(c) &&
        !seenSourceIds.has(c.id),
    );
    for (const s of sources) {
      seenSourceIds.add(s.id);
    }
    if (sources.length > 0) {
      groups.push({
        ancestorId: parent.id,
        ancestorName: parent.name?.trim() || 'Folder',
        depth,
        sources,
      });
    }
    parentId = parent.parentId;
    depth += 1;
  }
  return groups;
}

export function flattenAddMatchingContentSourceGroups(groups: AddMatchingContentSourceGroup[]): Category[] {
  return groups.flatMap((g) => g.sources);
}

export type CompareContentTarget =
  | { kind: 'contentCategory'; category: Category }
  | { kind: 'branch'; category: Category };

export type CompareContentTargetGroup = {
  ancestorId: string;
  ancestorName: string;
  depth: number;
  targets: CompareContentTarget[];
};

/**
 * True when a content category has rows (loaded) or the server explicitly reports rows.
 * Unknown/missing hints are treated as empty so pickers do not offer empty categories.
 */
export function contentCategoryMayHaveRows(category: Category): boolean {
  if (!category.contentCategory) return false;
  if (categoryContentsAreLoaded(category)) {
    return (category.contents?.length ?? 0) > 0;
  }
  return category.hasContents === true;
}

export function categorySubtreeHasContentRows(category: Category): boolean {
  if (category.contentCategory) {
    return contentCategoryMayHaveRows(category);
  }
  if (!branchChildrenAreLoaded(category)) {
    // Unloaded branch: keep it visible/navigable; may contain content once expanded.
    return category.hasChildCategories === true;
  }
  return (category.children ?? []).some(child => categorySubtreeHasContentRows(child));
}

/** Total content rows in this node (content category) or its entire subtree (branch). */
export function countContentRowsInCategorySubtree(category: Category): number {
  if (category.contentCategory) {
    return category.contents?.length ?? 0;
  }
  return (category.children ?? []).reduce(
    (sum, child) => sum + countContentRowsInCategorySubtree(child),
    0,
  );
}

/**
 * Row count for category-picker labels. Returns {@code null} when the count is not known yet
 * (lazy-loaded contents/children not fetched), so the UI does not show a misleading {@code (0)}.
 */
export function contentCountForPickerDisplay(category: Category): number | null {
  if (category.contentCategory) {
    if (categoryContentsAreLoaded(category)) {
      return category.contents?.length ?? 0;
    }
    if (category.hasContents === true) {
      return null;
    }
    return 0;
  }
  if (!branchChildrenAreLoaded(category)) {
    if (category.hasChildCategories === true) {
      return null;
    }
    return 0;
  }
  let total = 0;
  for (const child of category.children ?? []) {
    const childCount = contentCountForPickerDisplay(child);
    if (childCount === null) {
      return null;
    }
    total += childCount;
  }
  return total;
}

/**
 * Whether a category scope may have content rows for AI report (toolbar/context menu).
 * Returns false when a loaded subtree is known to be empty; defers to preview when counts are unknown.
 */
export function categoryReportScopeMayHaveRows(category: Category): boolean {
  const count = contentCountForPickerDisplay(category);
  if (count === 0) {
    return false;
  }
  if (count !== null && count > 0) {
    return true;
  }
  if (category.contentCategory) {
    return contentCategoryMayHaveRows(category);
  }
  return category.hasChildCategories === true || (category.children?.length ?? 0) > 0;
}

/** Content categories and branch folders along the path from source up to the document root. */
export function collectCompareContentTargetGroups(
  categoriesTree: Category[],
  source: Category,
): CompareContentTargetGroup[] {
  if (!source.contentCategory) {
    return [];
  }
  const groups: CompareContentTargetGroup[] = [];
  const seenTargetIds = new Set<string>();
  let depth = 0;
  let parentId = source.parentId;
  while (parentId) {
    const parent = findCategoryInTree(categoriesTree, parentId);
    if (!parent) {
      break;
    }
    const targets: CompareContentTarget[] = [];
    for (const child of parent.children ?? []) {
      if (seenTargetIds.has(child.id)) {
        continue;
      }
      if (child.contentCategory) {
        if (child.id === source.id || !contentCategoryMayHaveRows(child)) {
          continue;
        }
        seenTargetIds.add(child.id);
        targets.push({ kind: 'contentCategory', category: child });
      } else if (categorySubtreeHasContentRows(child)) {
        seenTargetIds.add(child.id);
        targets.push({ kind: 'branch', category: child });
      }
    }
    if (targets.length > 0) {
      groups.push({
        ancestorId: parent.id,
        ancestorName: parent.name?.trim() || 'Folder',
        depth,
        targets,
      });
    }
    parentId = parent.parentId;
    depth += 1;
  }
  return groups;
}

export function encodeCompareContentTargetValue(target: CompareContentTarget): string {
  return `${target.kind}:${target.category.id}`;
}

export function decodeCompareContentTargetValue(
  value: string,
  groups: CompareContentTargetGroup[],
): CompareContentTarget | null {
  const sep = value.indexOf(':');
  if (sep <= 0) {
    return null;
  }
  const kind = value.slice(0, sep);
  const id = value.slice(sep + 1);
  if (kind !== 'contentCategory' && kind !== 'branch') {
    return null;
  }
  for (const group of groups) {
    for (const t of group.targets) {
      if (t.category.id === id && t.kind === kind) {
        return t;
      }
    }
  }
  return null;
}

/** Walk from a category up to the document root node in the loaded tree. */
export function findDocumentRootCategory(
  categoriesTree: Category[],
  fromCategoryId: string,
): Category | null {
  let current = findCategoryInTree(categoriesTree, fromCategoryId);
  if (!current) {
    return categoriesTree[0] ?? null;
  }
  while (current.parentId) {
    const parent = findCategoryInTree(categoriesTree, current.parentId);
    if (!parent) {
      break;
    }
    current = parent;
  }
  return current;
}

/** Whether this node can be chosen as compare scope B (not the source content category). */
export function resolveCompareTargetForCategory(
  category: Category,
  sourceContentCategoryId: string,
): CompareContentTarget | null {
  if (category.id === sourceContentCategoryId) {
    return null;
  }
  if (category.contentCategory) {
    if (!contentCategoryMayHaveRows(category)) {
      return null;
    }
    return { kind: 'contentCategory', category };
  }
  if (categorySubtreeHasContentRows(category)) {
    return { kind: 'branch', category };
  }
  return null;
}

export function categorySubtreeHasSelectableCompareTarget(
  category: Category,
  sourceContentCategoryId: string,
): boolean {
  if (resolveCompareTargetForCategory(category, sourceContentCategoryId)) {
    return true;
  }
  if (!category.contentCategory && !branchChildrenAreLoaded(category)) {
    return category.hasChildCategories === true;
  }
  return (category.children ?? []).some(child =>
    categorySubtreeHasSelectableCompareTarget(child, sourceContentCategoryId),
  );
}

export function documentHasCompareTargets(
  categoriesTree: Category[],
  source: Category,
): boolean {
  const root = findDocumentRootCategory(categoriesTree, source.id);
  if (!root) {
    return false;
  }
  return categorySubtreeHasSelectableCompareTarget(root, source.id);
}

/** True if this node or any descendant is in {@code allowedIds}. */
export function categorySubtreeHasAllowedCategoryId(
  category: Category,
  allowedIds: Set<string>,
): boolean {
  if (allowedIds.has(category.id)) {
    return true;
  }
  if (!category.contentCategory && !branchChildrenAreLoaded(category)) {
    // Unloaded branch may contain an allowed source once expanded.
    return category.hasChildCategories === true;
  }
  return (category.children ?? []).some(child =>
    categorySubtreeHasAllowedCategoryId(child, allowedIds),
  );
}

/** Whether a picker tree node should render an expand control (loaded children or unload hint). */
export function categoryPickerNodeExpandable(category: Category): boolean {
  return categoryShowsBranchExpandable(category);
}

/** Default source: walk ancestors nearest-first — Temp stash for that folder, then Original Files Content. */
export const findDefaultAddMatchingContentSourceIdAmongGroups = (
  groups: AddMatchingContentSourceGroup[],
  targetCategoryId: string,
): string | undefined => {
  for (const group of groups) {
    const ancestorAsParent: Category = {
      id: group.ancestorId,
      name: group.ancestorName,
      children: group.sources,
      contentCategory: false,
    } as Category;
    const id = findDefaultAddMatchingContentSourceIdAtParent(ancestorAsParent, targetCategoryId);
    if (id) {
      return id;
    }
  }
  return undefined;
};

/** Default among content categories under one ancestor folder. */
export const findDefaultAddMatchingContentSourceIdAtParent = (
  parent: Category | null | undefined,
  targetCategoryId: string,
): string | undefined => {
  if (!parent?.children?.length) return undefined;
  const siblings = parent.children.filter(
    (c) => c.contentCategory && c.id !== targetCategoryId && contentCategoryMayHaveRows(c),
  );
  const byName = (name: string) =>
    siblings.find((c) => normalizeCategoryName(c.name) === normalizeCategoryName(name))?.id;
  if (parent.name?.trim()) {
    const tempId = byName(`${TEMP_CONTENT_CATEGORY_PREFIX}${parent.name.trim()}`);
    if (tempId) return tempId;
  }
  return byName(ORIGINAL_FILES_CONTENT_CATEGORY_NAME);
};

/** @deprecated Use findDefaultAddMatchingContentSourceIdAtParent — kept for call sites passing immediate parent only. */
export const findDefaultAddMatchingContentSourceId = findDefaultAddMatchingContentSourceIdAtParent;

export const getContentIndex = (contentId: string): string => {
  const lastIndex = contentId.lastIndexOf(":");
  if (lastIndex === -1) {
    // Delimiter not found, return the original string or handle as needed
    return contentId;
  }
  return contentId.substring(lastIndex + 1);
};

/** 1-based display index parsed from a content id suffix (stored 0-based on the server). */
export function getContentDisplayIndex(contentId: string): number | null {
  const raw = getContentIndex(contentId);
  if (!/^\d+$/.test(raw)) return null;
  const zeroBased = parseInt(raw, 10);
  if (Number.isNaN(zeroBased) || zeroBased < 0) return null;
  return zeroBased + 1;
}

/** Content rows visible under the content-pane subcase filter (`null` = no filter). */
export function countContentsMatchingSubcaseFilter(
  contents: Content[] | undefined,
  subcaseFilter: string[] | null | undefined,
): number {
  if (!contents?.length) return 0;
  if (subcaseFilter == null) return contents.length;
  return contents.filter((c) => subcaseFilter.includes(c.subcase)).length;
}

/** Invert multi-select within a fixed pool (e.g. document subcases in a filter dialog). */
export function invertSubcaseSelection(pool: string[], selected: string[]): string[] {
  const taken = new Set(selected);
  return pool.filter((s) => !taken.has(s));
}

/** Whether a content category should show the empty icon (truly empty or nothing matches the filter). */
export function isContentCategoryEmptyForDisplay(
  category: Category,
  subcaseFilter: string[] | null | undefined,
): boolean {
  if (!category.contentCategory) return false;
  if (!categoryContentsAreLoaded(category)) {
    // Rows not fetched yet — only show empty when the server says there are none.
    return category.hasContents === false;
  }
  return countContentsMatchingSubcaseFilter(category.contents, subcaseFilter) === 0;
}